import { useEffect, useRef, useState } from "react";
import { QrCode, Smartphone, Check, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { normRecipient } from "@/lib/blessed";
import { updateMyQr, fileToDataUrl, isPhoneish } from "@/lib/external";

const METHODS = ["zelle", "venmo"];
const cap = (m) => (m === "zelle" ? "Zelle" : "Venmo");

// Recipient-only: manage Zelle/Venmo QR images + handles so neighbors can send directly.
export default function PayoutMethods({ recipientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [handleModal, setHandleModal] = useState(null); // { method }
  const [handleInput, setHandleInput] = useState("");
  const zelleFile = useRef(null);
  const venmoFile = useRef(null);
  const fileRef = (m) => (m === "zelle" ? zelleFile : venmoFile);

  // Load from /recipients/me — the ONLY recipient endpoint that returns the payout
  // fields (zelle/venmo handle + QR). /recipients/{id} omits them.
  const load = () =>
    api.get(`/recipients/me`).then(({ data }) => setData(normRecipient(data))).catch(() => {});
  useEffect(() => { if (recipientId) load(); /* eslint-disable-next-line */ }, [recipientId]);

  if (!recipientId) return null;

  const qrOf = (m) => (m === "zelle" ? data?.zelleQr : data?.venmoQr);
  const handleOf = (m) => (m === "zelle" ? data?.zelleHandle : data?.venmoHandle);
  // Venmo usernames show with a leading @; a phone number shows as-is.
  const showHandle = (m, h) => (m === "venmo" && !isPhoneish(h) ? `@${(h || "").replace(/^@/, "")}` : h);
  const openFilePicker = (m) => fileRef(m).current?.click();

  const onFile = async (m, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await fileToDataUrl(file);
      if (url.length > 900000) { toast.error("QR image too large (~700KB max). Try a smaller screenshot."); return; }
      await updateMyQr(m, url); // saves new QR; handle untouched
      await load();
      setData((d) => ({ ...(d || {}), [m === "zelle" ? "zelleQr" : "venmoQr"]: url }));
      toast.success(`${cap(m)} QR saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save QR."); }
    finally { setBusy(false); }
  };

  const removeQr = async (m) => {
    setBusy(true);
    try {
      await updateMyQr(m, null); // clear_qr: true
      await load();
      setData((d) => ({ ...(d || {}), [m === "zelle" ? "zelleQr" : "venmoQr"]: null }));
      toast.success("QR removed");
    } catch { toast.error("Could not remove QR."); }
    finally { setBusy(false); }
  };

  const saveHandle = async () => {
    const m = handleModal.method;
    const val = handleInput.trim();
    if (!val) { toast.error("Enter a value first."); return; }
    setBusy(true);
    try {
      await updateMyQr(m, undefined, { handle: val }); // QR left untouched by the backend
      setHandleModal(null); setHandleInput("");
      await load();
      const stored = m === "venmo" ? val.replace(/^@/, "") : val;
      setData((d) => ({ ...(d || {}), [m === "zelle" ? "zelleHandle" : "venmoHandle"]: stored }));
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail ? `Save failed: ${e.response.data.detail}` : "Could not save."); }
    finally { setBusy(false); }
  };

  const removeHandle = async (m) => {
    setBusy(true);
    try {
      await updateMyQr(m, undefined, { clearHandle: true }); // QR untouched
      await load();
      setData((d) => ({ ...(d || {}), [m === "zelle" ? "zelleHandle" : "venmoHandle"]: null }));
      toast.success("Removed");
    } catch { toast.error("Could not remove."); }
    finally { setBusy(false); }
  };

  const editHandle = (m) => { setHandleModal({ method: m }); setHandleInput(handleOf(m) || ""); };

  const unsetCls = "flex-1 py-2.5 px-2 rounded-pill border text-xs font-black flex items-center justify-center gap-1.5 transition truncate bg-surface-3 border-border text-ink hover:bg-surface-2";
  const setCls = "flex-1 py-2 pl-3 pr-2 rounded-pill border text-xs font-black flex items-center gap-1.5 bg-[#DCFCE7] border-successb text-[#166534]";

  return (
    <div className="bg-surface-2 border border-border rounded-lg p-4 mb-5" data-testid="payout-methods">
      <div className="font-black text-sm mb-1">Zelle / Venmo (optional)</div>
      <p className="text-xs text-ink-tertiary mb-3">Let neighbors send you directly — they pay a ${2} card fee and you get the full amount. Tap to edit, trash to remove.</p>

      <div className="flex gap-2 mb-2">
        {METHODS.map((m) => {
          if (!qrOf(m)) return (
            <button key={`qr-${m}`} data-testid={`qr-chip-${m}`} disabled={busy} onClick={() => openFilePicker(m)} className={unsetCls}>
              <QrCode size={14} /> + Add {cap(m)} QR
            </button>
          );
          return (
            <div key={`qr-${m}`} className={setCls}>
              <button data-testid={`qr-chip-${m}`} disabled={busy} onClick={() => openFilePicker(m)}
                className="flex items-center gap-1.5 flex-1 min-w-0 justify-center">
                <Check size={14} /> <span className="truncate">{cap(m)} QR</span> <Pencil size={12} className="opacity-60" />
              </button>
              <button data-testid={`qr-remove-${m}`} disabled={busy} onClick={() => removeQr(m)} aria-label={`Remove ${cap(m)} QR`}
                className="shrink-0 ml-1 w-6 h-6 grid place-items-center rounded-pill hover:bg-black/10 text-[#166534]">
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        {METHODS.map((m) => {
          const h = handleOf(m);
          if (!h) return (
            <button key={`h-${m}`} data-testid={`handle-chip-${m}`} disabled={busy} onClick={() => editHandle(m)} className={unsetCls}>
              <Smartphone size={14} /> <span className="truncate">+ {m === "zelle" ? "Zelle phone" : "Venmo phone or @user"}</span>
            </button>
          );
          return (
            <div key={`h-${m}`} className={setCls}>
              <button data-testid={`handle-chip-${m}`} disabled={busy} onClick={() => editHandle(m)}
                className="flex items-center gap-1.5 flex-1 min-w-0 justify-center">
                <Smartphone size={14} /> <span className="truncate">{showHandle(m, h)}</span> <Pencil size={12} className="opacity-60" />
              </button>
              <button data-testid={`handle-remove-${m}`} disabled={busy} onClick={() => removeHandle(m)} aria-label={`Remove ${cap(m)} handle`}
                className="shrink-0 ml-1 w-6 h-6 grid place-items-center rounded-pill hover:bg-black/10 text-[#166534]">
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
      </div>

      <input ref={zelleFile} type="file" accept="image/*" className="hidden" data-testid="qr-file-zelle"
        onChange={(e) => { onFile("zelle", e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={venmoFile} type="file" accept="image/*" className="hidden" data-testid="qr-file-venmo"
        onChange={(e) => { onFile("venmo", e.target.files?.[0]); e.target.value = ""; }} />

      <Dialog open={!!handleModal} onOpenChange={(o) => !o && setHandleModal(null)}>
        <DialogContent className="max-w-sm bg-surface-2 border-border">
          <DialogHeader><DialogTitle>{handleModal?.method === "zelle" ? "Zelle phone number" : "Enter your Venmo @user or phone"}</DialogTitle></DialogHeader>
          <Input data-testid="handle-input" value={handleInput} onChange={(e) => setHandleInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveHandle(); } }}
            type="text" name="payout-handle" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} inputMode="text" enterKeyHint="done" data-1p-ignore data-lpignore="true"
            placeholder={handleModal?.method === "zelle" ? "e.g. 555-123-4567" : "@myuser or 555-123-4567"} className="bg-surface border-border" />
          <Button data-testid="handle-save" disabled={busy} onClick={saveHandle} className="w-full h-11 rounded-pill font-black bg-brand-primary text-white">
            {busy ? <Loader2 className="animate-spin" size={18} /> : "Save"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
